import { useState, useEffect, useRef } from 'react'
import { useListsStore, countCards } from '../../stores/listsStore'
import type { DeckList } from '../../stores/listsStore'
import ListEditor from './ListEditor'
import Button from '../ui/Button'
import type { Tcg } from '../../types'
import { parseDecklist, decklistToText } from '../../utils/decklist'

/** Short badge label per TCG. */
const TCG_BADGE: Record<Tcg, string> = {
  mtg: 'MTG',
  yugioh: 'YGO',
  onepiece: 'OP',
  lorcana: 'LOR',
  riftbound: 'RB',
}

/** Badge color per TCG. */
const TCG_BADGE_CLASS: Record<Tcg, string> = {
  mtg: 'bg-amber-100 text-amber-800',
  yugioh: 'bg-purple-100 text-purple-800',
  onepiece: 'bg-red-100 text-red-800',
  lorcana: 'bg-sky-100 text-sky-800',
  riftbound: 'bg-emerald-100 text-emerald-800',
}

interface ListsPanelProps {
  /** Generate a PDF for a single list. */
  onGeneratePdf: (list: DeckList, includeSideboard: boolean) => void
  /** Generate a combined PDF from several lists (mainboards; sideboard per list optional). */
  onGenerateCombined: (lists: DeckList[], includeSideboard: boolean) => void
}

export default function ListsPanel({ onGeneratePdf, onGenerateCombined }: ListsPanelProps) {
  const lists = useListsStore((s) => s.lists)
  const clearAll = useListsStore((s) => s.clearAll)
  const removeList = useListsStore((s) => s.removeList)
  const setListCards = useListsStore((s) => s.setListCards)
  const [openId, setOpenId] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [combineSide, setCombineSide] = useState(true)
  const [reParsingId, setReParsingId] = useState<string | null>(null)
  const [reParseText, setReParseText] = useState('')

  // Track previous list IDs to detect new additions
  const prevListIdsRef = useRef<Set<string>>(new Set())

  // Auto-select newly added lists
  useEffect(() => {
    const currentIds = new Set(lists.map((l) => l.id))
    const newIds = [...currentIds].filter((id) => !prevListIdsRef.current.has(id))

    if (newIds.length > 0) {
      setSelected((prev) => {
        const next = new Set(prev)
        newIds.forEach((id) => next.add(id))
        return next
      })
    }

    prevListIdsRef.current = currentIds
  }, [lists])

  if (lists.length === 0) return null

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedLists = lists.filter((l) => selected.has(l.id))
  const anySideInSelection = selectedLists.some((l) => l.sideboard.length > 0)
  const selectedGames = new Set(selectedLists.map((l) => l.tcg))
  const isMixed = selectedGames.size > 1
  const allSelected = lists.length > 0 && selected.size === lists.length

  const toggleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      setSelected(new Set())
    } else {
      // Select all
      setSelected(new Set(lists.map((l) => l.id)))
    }
  }

  const startReparse = (list: DeckList) => {
    setReParseText(decklistToText(list.mainboard, list.sideboard))
    setReParsingId(list.id)
  }

  const applyReparse = (listId: string) => {
    const { mainboard, sideboard } = parseDecklist(reParseText)
    setListCards(listId, mainboard, sideboard)
    setReParsingId(null)
  }

  const cancelReparse = () => {
    setReParsingId(null)
    setReParseText('')
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-900">
            Tus listas <span className="text-gray-400 font-normal">({lists.length})</span>
          </h2>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={allSelected}
              onChange={toggleSelectAll}
              title="Seleccionar todas las listas"
            />
            Todas
          </label>
        </div>
        <button
          className="text-sm text-red-600 hover:text-red-700"
          onClick={() => {
            if (window.confirm('¿Borrar TODAS las listas? Esta acción no se puede deshacer.')) {
              clearAll()
              setSelected(new Set())
              setOpenId(null)
            }
          }}
        >
          Borrar todas
        </button>
      </div>

      {/* List rows */}
      <ul className="space-y-2">
        {lists.map((list) => {
          const mainCount = countCards(list.mainboard)
          const sideCount = countCards(list.sideboard)
          const isOpen = openId === list.id
          const isReParsing = reParsingId === list.id

          return (
            <li key={list.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              {/* Summary row */}
              <div className="flex items-center gap-3 px-3 py-2.5">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 shrink-0"
                  checked={selected.has(list.id)}
                  onChange={() => toggleSelect(list.id)}
                  title="Seleccionar para combinar"
                />
                <button
                  className="flex-1 flex items-center justify-between gap-2 text-left min-w-0"
                  onClick={() => setOpenId(isOpen ? null : list.id)}
                >
                  <span
                    className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${TCG_BADGE_CLASS[list.tcg]}`}
                    title={`Juego: ${list.tcg}`}
                  >
                    {TCG_BADGE[list.tcg]}
                  </span>
                  <span className="font-medium text-gray-900 truncate">📋 {list.name}</span>
                  <span className="text-sm text-gray-500 shrink-0">
                    {mainCount} cartas{sideCount > 0 ? ` +${sideCount} sb` : ''}
                    <span className="ml-2 text-gray-400">{isOpen ? '▲' : '▼'}</span>
                  </span>
                </button>
                {/* Quick actions - visible without expanding */}
                <div className="flex gap-1 shrink-0">
                  <button
                    className="px-2 py-1 text-xs rounded border border-blue-200 text-blue-600 hover:bg-blue-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      startReparse(list)
                    }}
                    title="Re-pegar lista"
                  >
                    📝
                  </button>
                  <button
                    className="px-2 py-1 text-xs rounded border border-red-200 text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (window.confirm(`¿Borrar la lista "${list.name}"?`)) {
                        removeList(list.id)
                      }
                    }}
                    title="Borrar lista"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Re-parse mode */}
              {isReParsing && (
                <div className="border-t border-gray-100 p-3 space-y-2">
                  <textarea
                    className="w-full h-56 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    value={reParseText}
                    onChange={(e) => setReParseText(e.target.value)}
                    placeholder="Pega aquí la lista de cartas..."
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => applyReparse(list.id)}>Aplicar cambios</Button>
                    <button
                      className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                      onClick={cancelReparse}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Expanded editor */}
              {isOpen && !isReParsing && (
                <div className="border-t border-gray-100 p-3">
                  <ListEditor list={list} onGeneratePdf={onGeneratePdf} />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {/* Combine selected */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <span className="text-sm text-blue-900">
            {selected.size} lista(s) seleccionada(s)
          </span>
          {anySideInSelection && (
            <label className="flex items-center gap-2 text-sm text-blue-900">
              <input
                type="checkbox"
                className="rounded border-blue-300"
                checked={combineSide}
                onChange={(e) => setCombineSide(e.target.checked)}
              />
              Incluir sideboards
            </label>
          )}
          <Button onClick={() => onGenerateCombined(selectedLists, combineSide)}>
            Generar PDF combinado
          </Button>
          {isMixed && (
            <span className="text-xs text-blue-800 bg-blue-100 rounded px-2 py-1">
              Pedido mixto: se generará un PDF por juego dentro de un ZIP
            </span>
          )}
        </div>
      )}
    </div>
  )
}
